import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

/**
 * Formulário público pra denunciar documento suspeito.
 * Acessado pelo link "Denunciar abuso" no rodapé do e-mail de convite
 * ou diretamente (sem token).
 */

interface SignerContext {
  documentName: string
  documentSentAt: string
  signerEmail: string
  signerName: string
  senderName: string
  senderDomain: string | null
}

export default function ReportAbuse() {
  const [params] = useSearchParams()
  const token = params.get('token')

  const [context, setContext] = useState<SignerContext | null>(null)
  const [contextErr, setContextErr] = useState('')
  const [reporterEmail, setReporterEmail] = useState('')
  const [reporterName, setReporterName] = useState('')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!token) return
    fetch(`/api/abuse/${encodeURIComponent(token)}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setContext)
      .catch(() => setContextErr('Link de denúncia inválido ou expirado. Você ainda pode denunciar sem token.'))
  }, [token])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (reason.trim().length < 10) {
      setError('Descreva o motivo com pelo menos 10 caracteres.')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch('/api/abuse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token || undefined,
          reporterEmail: reporterEmail.trim() || undefined,
          reporterName: reporterName.trim() || undefined,
          reason: reason.trim(),
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.message || body.error || `HTTP ${res.status}`)
      }
      setSubmitted(true)
    } catch (err: any) {
      setError(err.message || 'Falha ao enviar denúncia')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-gray-200 p-8 max-w-md text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Denúncia recebida</h1>
          <p className="text-gray-600 mb-6">
            Nossa equipe vai analisar e tomar as providências cabíveis. Você não precisa fazer mais nada.
          </p>
          <Link to="/" className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm">
            Voltar ao site
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-indigo-600">PAKT</Link>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Denunciar abuso</h1>
        <p className="text-gray-600 mb-6">
          Use este formulário se você recebeu um documento que não esperava, que parece fraudulento,
          ou se o remetente está te assediando.
        </p>

        {context && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-sm">
            <div className="font-semibold text-gray-900 mb-2">Documento que está sendo denunciado:</div>
            <dl className="space-y-1 text-gray-700">
              <div className="flex gap-2"><dt className="font-medium w-32">Nome:</dt><dd>{context.documentName}</dd></div>
              <div className="flex gap-2"><dt className="font-medium w-32">Remetente:</dt><dd>{context.senderName} {context.senderDomain && <span className="text-gray-400">(@{context.senderDomain})</span>}</dd></div>
              <div className="flex gap-2"><dt className="font-medium w-32">Enviado pra:</dt><dd>{context.signerName} — <span className="text-gray-500 font-mono">{context.signerEmail}</span></dd></div>
              <div className="flex gap-2"><dt className="font-medium w-32">Em:</dt><dd>{new Date(context.documentSentAt).toLocaleString('pt-BR')}</dd></div>
            </dl>
          </div>
        )}

        {contextErr && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded mb-6 text-sm">
            {contextErr}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">
              Por que está denunciando? *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={6}
              maxLength={5000}
              placeholder="Ex: não conheço o remetente, não esperava esse documento, o conteúdo parece fraudulento, recebi vários convites do mesmo tipo, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">{reason.length}/5000</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Seu e-mail (opcional)
              </label>
              <input
                type="email"
                value={reporterEmail}
                onChange={(e) => setReporterEmail(e.target.value)}
                placeholder="Pra retornarmos se precisar"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Seu nome (opcional)
              </label>
              <input
                type="text"
                value={reporterName}
                onChange={(e) => setReporterName(e.target.value)}
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm">{error}</div>
          )}

          <button
            type="submit"
            disabled={submitting || reason.trim().length < 10}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-semibold disabled:opacity-50"
          >
            {submitting ? 'Enviando...' : 'Enviar denúncia'}
          </button>

          <p className="text-xs text-gray-500 mt-4">
            Sua denúncia é avaliada por nossa equipe de Compliance. Em casos confirmados,
            o documento é cancelado e a conta do remetente é suspensa. Veja a{' '}
            <Link to="/ajuda" className="text-indigo-600 hover:underline">Central de Ajuda</Link>
            {' '}pra outras dúvidas.
          </p>
        </form>
      </main>
    </div>
  )
}
